import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, X, Search, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const SubstitutionsList = () => {
  const [substitutions, setSubstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSubstitution, setSelectedSubstitution] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const { user: currentUser } = useAuth();

  const [formData, setFormData] = useState({
    substituteId: '',
    startDate: '',
    endDate: '',
    active: true,
    transferOpenInvoices: false
  });
  const [openInvoiceStats, setOpenInvoiceStats] = useState({ total: 0, listCount: 0, inProgressCount: 0 });
  const [additionalPermissions, setAdditionalPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [substituteSearchQuery, setSubstituteSearchQuery] = useState('');
  const [substituteDropdownOpen, setSubstituteDropdownOpen] = useState(false);
  const substituteDropdownRef = useRef(null);

  useEffect(() => {
    fetchSubstitutions();
    fetchAllUsers();
  }, [currentUser]);
  
  // Zamknij dropdown gdy kliknięto poza nim
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (substituteDropdownRef.current && !substituteDropdownRef.current.contains(event.target)) {
        setSubstituteDropdownOpen(false);
      }
    };
    
    if (substituteDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [substituteDropdownOpen]);

  const fetchSubstitutions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/substitutions');
      setSubstitutions(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania zastępstw');
      console.error('Error fetching substitutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/substitutions/all-users');
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Błąd podczas pobierania użytkowników');
    }
  };

  const fetchOpenInvoiceStats = async () => {
    try {
      const response = await api.get('/substitutions/open-invoices-count');
      setOpenInvoiceStats({
        total: response.data?.total || 0,
        listCount: response.data?.listCount || 0,
        inProgressCount: response.data?.inProgressCount || 0
      });
    } catch (error) {
      console.error('Error fetching open invoices count:', error);
      setOpenInvoiceStats({ total: 0, listCount: 0, inProgressCount: 0 });
    }
  };

  const handleCreateClick = () => {
    setSelectedSubstitution(null);
    setFormData({
      substituteId: '',
      startDate: '',
      endDate: '',
      active: true,
      transferOpenInvoices: false
    });
    setAdditionalPermissions([]);
    setSubstituteSearchQuery('');
    setSubstituteDropdownOpen(false);
    setOpenInvoiceStats({ total: 0, listCount: 0, inProgressCount: 0 });
    setShowModal(true);
    fetchOpenInvoiceStats();
  };

  const handleEditClick = (substitution) => {
    setSelectedSubstitution(substitution);
    setFormData({
      substituteId: substitution.substituteId.toString(),
      startDate: substitution.startDate,
      endDate: substitution.endDate,
      active: substitution.active,
      transferOpenInvoices: false
    });
    setAdditionalPermissions(substitution.additionalPermissions || []);
    setSubstituteSearchQuery('');
    setSubstituteDropdownOpen(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSubstitution(null);
    setFormData({
      substituteId: '',
      startDate: '',
      endDate: '',
      active: true,
      transferOpenInvoices: false
    });
    setOpenInvoiceStats({ total: 0, listCount: 0, inProgressCount: 0 });
    setAdditionalPermissions([]);
    setSubstituteSearchQuery('');
    setSubstituteDropdownOpen(false);
  };
  
  // Filtruj pracowników na podstawie wyszukiwania
  const filteredEmployees = employees.filter(emp => {
    if (!substituteSearchQuery) return true;
    const searchLower = substituteSearchQuery.toLowerCase();
    const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase();
    const email = (emp.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });
  
  // Pobierz wybranego pracownika
  const selectedEmployee = employees.find(emp => emp.id.toString() === formData.substituteId);
  
  const getPermissionDisplayName = (permissionName) => {
    const displayNames = {
      'canAccessSettings': 'Dostęp do ustawień',
      'canAccessUsers': 'Dostęp do użytkowników',
      'canAccessDepartments': 'Dostęp do działów',
      'canAccessCases': 'Dostęp do spraw',
      'canAccessHr': 'Dostęp do kadr',
      'canAccessPrints': 'Dostęp do wydruków',
      'canAccessUmowyCru': 'Dostęp do umów CRU',
      'canAccountInvoice': 'Zaksięgowanie faktur',
      'isManager': 'Status kierownika',
      'isDirector': 'Status dyrektora',
      'isPreviewMode': 'Tryb podglądu',
      'canCheckMerit': 'Sprawdzanie merytoryczne',
      'canDoPreliminaryCheck': 'Wstępna kontrola',
      'canCheckFormalAccounting': 'Kontrola formalno-rachunkowa',
      'canAcceptInvoices': 'Akceptacja faktur',
      'canViewSignedInvoices': 'Przeglądanie podpisanych faktur',
      'canViewAccountedInvoices': 'Przeglądanie zaksięgowanych faktur',
      'canDeleteDescriptionsAndSignatures': 'Usuwanie opisów i podpisów',
      'canDeleteInvoices': 'Usuwanie faktur',
      'canEditInvoices': 'Edycja faktur',
      'canUseCostCenter': 'Używanie centrów kosztów'
    };
    return displayNames[permissionName] || permissionName;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Walidacja
    if (!formData.substituteId) {
      toast.error('Wybierz zastępcę');
      return;
    }
    
    if (!formData.startDate) {
      toast.error('Wybierz datę rozpoczęcia');
      return;
    }
    
    if (!formData.endDate) {
      toast.error('Wybierz datę zakończenia');
      return;
    }
    
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('Data zakończenia nie może być wcześniejsza niż data rozpoczęcia');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        managerId: currentUser.id,
        substituteId: parseInt(formData.substituteId),
        startDate: formData.startDate,
        endDate: formData.endDate,
        active: formData.active,
        transferOpenInvoices: !selectedSubstitution && !!formData.transferOpenInvoices
      };

      let response;
      if (selectedSubstitution) {
        response = await api.put(`/substitutions/${selectedSubstitution.id}`, data);
        toast.success('Zastępstwo zostało zaktualizowane');
      } else {
        response = await api.post('/substitutions', data);
        const transferred = response.data?.transferredInvoiceCount || 0;
        toast.success(transferred > 0
          ? `Zastępstwo zostało utworzone. Przeniesiono ${transferred} ${transferred === 1 ? 'fakturę' : transferred < 5 ? 'faktury' : 'faktur'} do zastępcy.`
          : 'Zastępstwo zostało utworzone');
        
        // Pokaż informację o dodatkowych uprawnieniach jeśli zostały nadane
        if (response.data?.additionalPermissions && response.data.additionalPermissions.length > 0) {
          const permissionsList = response.data.additionalPermissions
            .map(p => getPermissionDisplayName(p))
            .join(', ');
          toast.success(`Zastępstwo utworzone. Zastępca otrzyma dodatkowe uprawnienia: ${permissionsList}`, {
            duration: 6000
          });
        }
      }
      
      handleCloseModal();
      fetchSubstitutions();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas zapisywania zastępstwa';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zastępstwo?')) {
      return;
    }

    try {
      await api.delete(`/substitutions/${id}`);
      toast.success('Zastępstwo zostało usunięte');
      fetchSubstitutions();
    } catch (error) {
      toast.error('Błąd podczas usuwania zastępstwa');
    }
  };


  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl">Zastępstwa</h2>
        </div>
        <div className="card-body flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  const activeSubstitutions = substitutions.filter(s => s.active);
  const inactiveSubstitutions = substitutions.filter(s => !s.active);

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-xl">Moje zastępstwa</h2>
        <button
          onClick={handleCreateClick}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Utwórz zastępstwo
        </button>
      </div>

      <div className="card-body">
        {activeSubstitutions.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-green-700">Aktywne zastępstwa</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ja</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zastępca</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data rozpoczęcia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data zakończenia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dodatkowe uprawnienia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akcje</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeSubstitutions.map((sub) => (
                    <tr key={sub.id}>
                      <td className="px-4 py-3 text-sm">{sub.managerName}</td>
                      <td className="px-4 py-3 text-sm">{sub.substituteName}</td>
                      <td className="px-4 py-3 text-sm">{sub.startDate}</td>
                      <td className="px-4 py-3 text-sm">{sub.endDate}</td>
                      <td className="px-4 py-3 text-sm">
                        {sub.additionalPermissions && sub.additionalPermissions.length > 0 ? (
                          <div className="text-xs text-blue-600">
                            +{sub.additionalPermissions.length} uprawnień
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">Brak dodatkowych</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditClick(sub)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {!currentUser?.isPreviewMode && (
                          <button
                            onClick={() => handleDelete(sub.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {inactiveSubstitutions.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gray-600">Historyczne zastępstwa</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ja</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zastępca</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data rozpoczęcia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data zakończenia</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inactiveSubstitutions.map((sub) => (
                    <tr key={sub.id} className="opacity-60">
                      <td className="px-4 py-3 text-sm">{sub.managerName}</td>
                      <td className="px-4 py-3 text-sm">{sub.substituteName}</td>
                      <td className="px-4 py-3 text-sm">{sub.startDate}</td>
                      <td className="px-4 py-3 text-sm">{sub.endDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {substitutions.length === 0 && (
          <p className="text-gray-600 text-center py-8">Brak zastępstw</p>
        )}
      </div>

      {/* Modal tworzenia/edycji */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedSubstitution ? 'Edytuj zastępstwo' : 'Utwórz zastępstwo'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Zastępca <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={substituteDropdownRef}>
                  <div
                    onClick={() => setSubstituteDropdownOpen(!substituteDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer flex items-center justify-between hover:border-gray-400"
                  >
                    <span className={selectedEmployee ? 'text-gray-900' : 'text-gray-500'}>
                      {selectedEmployee 
                        ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` 
                        : 'Wybierz zastępcę'}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${substituteDropdownOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                  
                  {substituteDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                      {/* Pole wyszukiwania */}
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Szukaj pracownika..."
                            value={substituteSearchQuery}
                            onChange={(e) => setSubstituteSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            autoFocus
                          />
                        </div>
                      </div>
                      
                      {/* Lista pracowników */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((emp) => (
                            <div
                              key={emp.id}
                              onClick={async () => {
                                const substituteId = emp.id.toString();
                                setFormData({ ...formData, substituteId });
                                setSubstituteDropdownOpen(false);
                                setSubstituteSearchQuery('');
                                
                                // Pobierz podgląd uprawnień dla wybranego zastępcy
                                if (!selectedSubstitution) {
                                  try {
                                    setLoadingPermissions(true);
                                    const response = await api.get(`/substitutions/permissions-preview?substituteId=${substituteId}`);
                                    setAdditionalPermissions(response.data?.permissions || []);
                                  } catch (error) {
                                    console.error('Error fetching permissions preview:', error);
                                    setAdditionalPermissions([]);
                                  } finally {
                                    setLoadingPermissions(false);
                                  }
                                } else {
                                  setAdditionalPermissions([]);
                                }
                              }}
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                                formData.substituteId === emp.id.toString() ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                              }`}
                            >
                              <div className="font-medium">{emp.firstName} {emp.lastName}</div>
                              {emp.email && (
                                <div className="text-xs text-gray-500">{emp.email}</div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm text-center">
                            Brak wyników
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {!formData.substituteId && (
                  <p className="mt-1 text-xs text-red-500">To pole jest wymagane</p>
                )}
              </div>
              
              {/* Wyświetl dodatkowe uprawnienia jeśli są dostępne */}
              {(additionalPermissions.length > 0 || (selectedSubstitution && selectedSubstitution.additionalPermissions && selectedSubstitution.additionalPermissions.length > 0)) && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="text-sm font-medium text-blue-900 mb-2">
                    Dodatkowe uprawnienia które otrzyma zastępca:
                  </div>
                  {loadingPermissions ? (
                    <div className="text-sm text-blue-600">Sprawdzanie uprawnień...</div>
                  ) : (
                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                      {(selectedSubstitution?.additionalPermissions || additionalPermissions).map((perm, idx) => (
                        <li key={idx}>{getPermissionDisplayName(perm)}</li>
                      ))}
                    </ul>
                  )}
                  {(!selectedSubstitution?.additionalPermissions && additionalPermissions.length === 0 && !loadingPermissions) && (
                    <div className="text-sm text-gray-600">Zastępca ma już wszystkie wymagane uprawnienia.</div>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Data rozpoczęcia <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Data zakończenia <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-4 w-4 text-primary-600"
                  id="active"
                />
                <label htmlFor="active" className="ml-2 text-sm text-gray-700">
                  Aktywne
                </label>
              </div>
              {!selectedSubstitution && openInvoiceStats.total > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.transferOpenInvoices}
                      onChange={(e) => setFormData({ ...formData, transferOpenInvoices: e.target.checked })}
                      className="h-4 w-4 mt-0.5 text-primary-600"
                      id="transferOpenInvoices"
                    />
                    <span className="text-sm text-gray-800">
                      <span className="font-medium">Przenieś niedokończone faktury do zastępcy</span>
                      <span className="block text-xs text-gray-600 mt-0.5">
                        {openInvoiceStats.total} {openInvoiceStats.total === 1 ? 'faktura' : openInvoiceStats.total < 5 ? 'faktury' : 'faktur'}
                        {openInvoiceStats.listCount > 0 ? ` na liście (${openInvoiceStats.listCount})` : ''}
                        {openInvoiceStats.listCount > 0 && openInvoiceStats.inProgressCount > 0 ? ' i' : ''}
                        {openInvoiceStats.inProgressCount > 0 ? ` w realizacji (${openInvoiceStats.inProgressCount})` : ''}
                      </span>
                    </span>
                  </label>
                </div>
              )}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Zapisywanie...' : 'Zapisz'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubstitutionsList;
